import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import isEqual from 'lodash/isEqual';
import { Address } from '@/models/locations';
import { ui } from '@/constants';
import { Restaurant } from '@/models';

const DARK_BROWN = '#ebe6e2';

const STYLES: google.maps.MapTypeStyle[] = [
  { stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', stylers: [{ visibility: 'on' }] },
  { featureType: 'landscape.man_made', stylers: [{ color: ui.colors.greyMedium }] },
  { featureType: 'landscape.natural', elementType: 'geometry.fill', stylers: [{ color: DARK_BROWN }] },
  { featureType: 'road.arterial', stylers: [{ visibility: 'on' }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: DARK_BROWN }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: DARK_BROWN }, { visibility: 'on' }] },
  { featureType: 'road.local', stylers: [{ visibility: 'on' }] },
  { featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ color: DARK_BROWN }] },
];

@Component({
  name: 'CmgCheckoutHeaderMap',
})
export default class CmgCheckoutHeaderMap extends Vue {
  isMapReady = false;

  @Prop({ required: true, type: Boolean })
  isPickup!: boolean;

  @Prop({ type: Object })
  address?: Address;

  @Prop({ type: Object })
  restaurant?: Restaurant;

  private mapsResolver!: Promise<typeof google>;
  private map?: google.maps.Map;
  private resizeListener?: () => void;
  private isLargeWidth?: boolean;

  @Watch('address')
  onAddressChanged(address: Address, oldAddress: Address) {
    this.$services.logger.debug('Refreshing map after address change');

    if (isEqual(address, oldAddress)) {
      this.$services.logger.debug('Addresses are the same -- aborting');
      return;
    }

    this.isLargeWidth = undefined;
    this.renderMap();
  }

  mounted() {
    this.resizeListener = () => {
      this.panForOffset();
    };

    window.addEventListener('resize', this.resizeListener);
    this.renderMap();
  }

  destroyed() {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  beforeCreate() {
    this.mapsResolver = this.$services.googleMaps.getGoogleMaps();
  }

  private async renderMap() {
    const google = await this.mapsResolver;

    if (!this.address) {
      this.$services.logger.debug('No address -- clearing pin and aborting from renderMap');
      return;
    }

    this.isMapReady = false;
    
    let isFavorite = this.isFavoriteRestaurant(this.restaurant?.restaurantNumber);
    const pinLocation = await this.getPinCoordinates(this.address);

    this.map = new google.maps.Map(this.$refs['map'] as Element, {
      center: pinLocation,
      zoom: 16,
      disableDefaultUI: true,
      styles: STYLES,
      draggableCursor: 'unset',
      draggingCursor: 'default',
      draggable: false,
    });

    google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
      this.panForOffset();
    });

    const icon = this.makePinIcon(this.isPickup, isFavorite);
    
    // tslint:disable-next-line: no-unused-expression
    new google.maps.Marker({
      position: pinLocation,
      map: this.map,
      icon,
      cursor: 'default',
    });

    this.isMapReady = true;
  }

  private makePinIcon(isPickup: boolean, isFavorite: boolean | undefined): google.maps.Icon {
    const icons = this.$appStore.state.cms.images.icons;
    const scaledSize = new google.maps.Size(90, 90);

    
    let url: string;
    if (!this.$services.restaurant.isRestaurantOpen(this.restaurant) || !this.restaurant?.onlineOrderingEnabled)
    {
      url = this.restaurant?.digitalKitchen ? icons.pepperMarkerClosed.default 
        :this.restaurant?.chipotlanePickupEnabled ? icons.pepperSwiftClosed.default 
        : this.restaurant?.walkupWindowEnabled ? icons.pepperSwiftClosed.default
        : icons.pepperMarkerClosed.default;
    }
    else if (isPickup && isFavorite)
    {
      url = this.restaurant?.digitalKitchen ? icons.pepperMarkerFave.default 
        :this.restaurant?.chipotlanePickupEnabled ? icons.pepperSwiftFavorite.default 
        : this.restaurant?.walkupWindowEnabled ? icons.pepperSwiftFavorite.default
        : icons.pepperMarkerFave.default;
    }
    else if (isPickup) {
      // Digital kitchen - show default pepper
      url = this.restaurant?.digitalKitchen ? icons.pepperMarker.default 
        :this.restaurant?.chipotlanePickupEnabled ? icons.pepperSwift.default 
        : this.restaurant?.walkupWindowEnabled ? icons.pepperSwift.default
        : icons.pepperMarker.default;
    } else {
      url = icons.deliveryMarker.default;
    }

    return { url, scaledSize };
  }

  private async getPinCoordinates(address: Address): Promise<google.maps.LatLng> {
    const { latitude, longitude, line1, locality, administrativeArea, postalCode } = address;

    // If the pin address has the lat & lng, just use that
    if (latitude && longitude) {
      return new google.maps.LatLng(latitude, longitude);
    }

    // Otherwise, try to geocode
    if (line1 && locality && administrativeArea && postalCode) {
      const search = [line1, locality, administrativeArea, postalCode].join(' ');
      const location = await this.$services.googleMaps.googleGeocodeSearchString(search);
      if (location) {
        return location;
      }
    }

    // Otherwise, give up!
    this.$services.logger.error('Not enough information on address to extract lat/lng or geocode', { address: this.address });
    throw new Error('Failed to find pin location for map!');
  }

  private panForOffset() {
    if (!this.map) {
      return;
    }

    const newIsLargeWidth = window.innerWidth >= ui.breakpoints.tablet;
    if (this.isLargeWidth === undefined && newIsLargeWidth) {
      // skip pan for desktop initial load
    } else if (this.isLargeWidth != newIsLargeWidth) {
      this.map.panBy(0, newIsLargeWidth ? 70 : -70);
    }
    this.isLargeWidth = newIsLargeWidth;
  }

  isFavoriteRestaurant(restaurantNumber: number | undefined) {
    if (restaurantNumber) {
      return this.$services.customer.isFavoriteRestaurant(restaurantNumber);
    }
  }
}
